import React from "react";
import { Modal, Show, Typography, Button, Alert, Tag } from "@pankod/refine-antd";
import { useNavigation } from "@pankod/refine-core";
import { rentPlaceStatusOptions, statusColors, placeColors } from "../../constants/rentPlace.constants";
import dayjs from "dayjs";
import 'dayjs/locale/es';

const { Title, Text } = Typography;

// Configurar dayjs en español
dayjs.locale('es');

interface RentPlaceDetailModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk: () => void;
  isLoading: boolean;
  showId: string | number;
  showRecord: any;
  hasEditPermission?: boolean;
}

export const RentPlaceDetailModal: React.FC<RentPlaceDetailModalProps> = ({
  visible,
  onCancel,
  onOk,
  isLoading,
  showId,
  showRecord,
  hasEditPermission = true
}) => {
  const { push } = useNavigation();

  const formatDateRange = () => {
    if (!showRecord?.start_date) return "--";
    
    const startDate = dayjs(showRecord.start_date);
    const endDate = showRecord.end_date ? dayjs(showRecord.end_date) : null;
    const isSameDay = endDate ? startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD') : true;
    
    let dateText = startDate.format('DD/MM/YYYY');
    if (!isSameDay && endDate) {
      dateText += ` - ${endDate.format('DD/MM/YYYY')}`;
    }
    
    return dateText;
  };

  return (
    <Modal 
      visible={visible} 
      onCancel={onCancel} 
      onOk={onOk} 
      width={800}
      footer={null}
      destroyOnClose={true}
      centered
    >
      <Show
        isLoading={isLoading}
        recordItemId={showId}
        resource="rent-places"
        headerProps={{
          title: "Detalle de Renta de Lugar",
        }}
        headerButtons={() => (
          <div className="event-modal-actions">
            {hasEditPermission && (
              <Button
                type="primary"
                onClick={() => push(`/rent-places/edit/${showRecord?.id}`)}
              >
                Editar
              </Button>
            )}
          </div>
        )}
      >
        {showRecord?.status === "CANCELED" && (
          <Alert
            message="Evento Cancelado"
            description="Este evento ha sido cancelado"
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <div className="event-detail-content">
          <div className="event-detail-row">
            <div className="event-detail-label">ID:</div>
            <div className="event-detail-value">{showRecord?.id}</div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Nombre del Evento:</div>
            <div className="event-detail-value event-title">{showRecord?.event_name || "--"}</div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Lugar:</div>
            <div className="event-detail-value">
              <Tag
                color={placeColors[showRecord?.place] || '#1890ff'}
                style={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  border: 'none',
                  fontSize: '14px',
                  padding: '4px 12px'
                }}
              >
                {showRecord?.place || "--"}
              </Tag>
            </div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Número de Recibo:</div>
            <div className="event-detail-value">{showRecord?.receipt_number || "--"}</div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Institución:</div>
            <div className="event-detail-value">{showRecord?.institution || "--"}</div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Fecha del evento:</div>
            <div className="event-detail-value event-date">{formatDateRange()}</div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Horario:</div>
            <div className="event-detail-value event-time">
              {showRecord?.start_time && showRecord?.end_time ? (
                <>De <strong>{showRecord.start_time}</strong> a <strong>{showRecord.end_time}</strong></>
              ) : showRecord?.start_time ? (
                <>A las <strong>{showRecord.start_time}</strong></>
              ) : "--"}
            </div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Responsable:</div>
            <div className="event-detail-value">{showRecord?.responsible_name || "--"}</div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Teléfono:</div>
            <div className="event-detail-value">{showRecord?.responsible_phone || "--"}</div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Tipo de Evento:</div>
            <div className="event-detail-value">
              <Tag
                color={showRecord?.is_free ? "#52c41a" : "#faad14"}
                style={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  border: 'none',
                  fontSize: '14px',
                  padding: '4px 12px'
                }}
              >
                {showRecord?.is_free ? "Gratuito" : "Con Costo"}
              </Tag>
            </div>
          </div>

          <div className="event-detail-row">
            <div className="event-detail-label">Estatus:</div>
            <div className="event-detail-value">
              <Tag
                color={statusColors[showRecord?.status] || '#1890ff'}
                style={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  border: 'none',
                  fontSize: '14px',
                  padding: '4px 12px'
                }}
              >
                {rentPlaceStatusOptions[showRecord?.status as keyof typeof rentPlaceStatusOptions] || showRecord?.status || "--"}
              </Tag>
            </div>
          </div>

          {showRecord?.status === "PAID" && (showRecord?.rental_cost !== undefined || showRecord?.rental_total !== undefined) && (
            <div className="event-detail-row">
              <div className="event-detail-label">Información de Pago:</div>
              <div className="event-detail-value">
                <div style={{ 
                  background: '#f6f9fc', 
                  padding: '16px', 
                  borderRadius: '6px', 
                  marginTop: '8px',
                  border: '1px solid #d9edf7'
                }}>
                  {showRecord?.rental_cost !== undefined && showRecord.rental_cost !== null && (
                    <div style={{ marginBottom: '8px' }}>
                      <Text><strong>Costo:</strong> ${Number(showRecord.rental_cost).toFixed(2)}</Text>
                    </div>
                  )}
                  {showRecord?.rental_discount !== undefined && showRecord.rental_discount !== null && Number(showRecord.rental_discount) > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <Text><strong>Descuento:</strong> ${Number(showRecord.rental_discount).toFixed(2)}</Text>
                    </div>
                  )}
                  {showRecord?.rental_total !== undefined && showRecord.rental_total !== null && (
                    <div style={{ marginBottom: showRecord?.payment_comment ? '12px' : '0' }}>
                      <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                        <strong>Total Pagado:</strong> ${Number(showRecord.rental_total).toFixed(2)}
                      </Text>
                    </div>
                  )}
                  {showRecord?.payment_comment && (
                    <div style={{ 
                      marginTop: '12px',
                      padding: '8px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '4px',
                      borderLeft: '3px solid #1890ff'
                    }}>
                      <Text style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                        Comentario de Pago:
                      </Text>
                      <Text style={{ 
                        whiteSpace: 'pre-wrap', 
                        fontStyle: 'italic',
                        display: 'block',
                        marginTop: '4px',
                        color: '#666'
                      }}>
                        "{showRecord.payment_comment}"
                      </Text>
                    </div>
                  )}
                  {showRecord?.needs_invoice !== undefined && (
                    <div style={{ 
                      marginTop: '12px',
                      padding: '8px',
                      background: showRecord.needs_invoice ? 'rgba(250, 173, 20, 0.1)' : 'rgba(82, 196, 26, 0.1)',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${showRecord.needs_invoice ? '#faad14' : '#52c41a'}`
                    }}>
                      <Text style={{ 
                        fontWeight: 'bold',
                        color: showRecord.needs_invoice ? '#faad14' : '#52c41a'
                      }}>
                        {showRecord.needs_invoice ? '📋 Requiere factura' : '✅ No requiere factura'}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {showRecord?.observations && (
            <div className="event-detail-row description-row">
              <div className="event-detail-label">Observaciones:</div>
              <div className="event-detail-value event-description">
                {showRecord.observations}
              </div>
            </div>
          )}
        </div>
      </Show>
    </Modal>
  );
};
